
import Vue from 'vue';
import { annotationSourceReference, canAnnotateImage } from '@/utils/annotationSource';
import { mapActions, mapGetters, mapState } from 'vuex';
import { RunnitBulkActionsMixin } from '@/mixins/RunnitBulkActionsMixin';
import RunnitSelectedImage from '@/views/Runnits/RunnitSelectedImage.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import ImageUpload from "@/views/Runnits/RunnitSettings/ImageInput/ImageUpload.vue";
import Use3DTooltipCard from '@/views/Runnits/RunnitSettings/Pose3DEditor/Use3DTooltipCard.vue';
import {
	IMAGE_UPLOAD_MODE,
	UploadFile,
	RunnitNodeRunResult,
} from '@run-diffusion/shared';
import { RunnitsImageSelectMixin } from "@/mixins/RunnitsImageSelectMixin";
import { NODE_RUN_RESULT_SELECTION_MODE } from '@/views/Runnits/constants';
import { TeamUserMixin } from '@/mixins';
import {
	shouldShowCommittedImageControls,
	shouldShowDesktopSelectionButton,
} from '@/views/Runnits/RunnitSettings/ImageInput/multiImageSelectionButton';
import { SNACKBAR_STATUS } from '@/constants/constants';

export default Vue.extend({
	name: 'MultiImageInputSelectedImageContainer',
	props: {
		value: { type: Object, default: null },
		libraryText: { type: String, default: 'Library' },
		field: { type: Object, default: null },
		maxImages: { type: Number, default: null },
	},
	mixins: [
		RunnitBulkActionsMixin,
		RunnitsImageSelectMixin,
		TeamUserMixin,
	],
	computed: {
		...mapState([
			'user',
			'runnitState',
			'selectionStateHistory',
			'dragAndDrop',
		]),
		...mapGetters([
			'getInpaintingEditorState',
		]),
		hasImages () {
			return !!(this.allImages && this.allImages.length > 0);
		},
		liveSelectionState (): any {
			return this.selectionStateHistory?.[this.selectionStateTriggerId] || null;
		},
		liveImages () {
			const uploads = this._get(this.liveSelectionState, 'selectedUploads') || [];
			const nodeRunResults = this._get(this.liveSelectionState, 'selectedNodeRunResults') || [];
			return [
				...uploads.map((upload: any) => ({ upload })),
				...nodeRunResults.map((nodeRunResult: any) => ({ nodeRunResult })),
			];
		},
		displayImages () {
			// During selection mode, show live selection in the preview stack + badge
			return this.isThisFieldSelectionActive ? this.liveImages : this.allImages;
		},
		displayImagesCount () {
			return (this.displayImages || []).length;
		},
		displayHasImages () {
			// Allow the preview stack to show during selection mode, even before the parent commits value
			return this.displayImagesCount > 0;
		},
		allImages () {
			const uploads = this._get(this.value, 'uploads') || [];
			const nodeRunResults = this._get(this.value, 'nodeRunResults') || [];
			return [
				...uploads.map(upload => ({ upload })),
				...nodeRunResults.map(nodeRunResult => ({ nodeRunResult })),
			];
		},
		displayedImages () {
			// Show up to 3 images in the stack
			return (this.displayImages || []).slice(0, 3);
		},
		remainingCount () {
			return Math.max(0, this.displayImagesCount - 3);
		},
		isDragging () {
			return (this.dragAndDrop as any)?.isDragging;
		},
		selectionStateTriggerId (): string {
			// Use field.uuid if available (matches MultiImageInput), otherwise fall back to fieldDefUuid
			if (this.field?.uuid) {
				return `multi-image-input-${this.field.uuid}`;
			}
			return `multi-image-input-${this.field.fieldDefUuid || ''}`;
		},
		isThisFieldSelectionActive (): boolean {
			return this.currentSelectionState?.triggeringId === this.selectionStateTriggerId;
		},
		hasVisualPrompt (): boolean {
			return !!this._get(this.field, 'display.hasVisualPrompt') &&
				[...(this.value?.uploads || []), ...(this.value?.nodeRunResults || [])].some(canAnnotateImage);
		},
		showDesktopSelectionButton (): boolean {
			return shouldShowDesktopSelectionButton({
				isDesktop: this.$vuetify.breakpoint.mdAndUp,
				hasCommittedImages: this.hasImages,
				isSelectionActive: this.isThisFieldSelectionActive,
			});
		},
		selectionModeButtonText (): string {
			return this.isThisFieldSelectionActive ? 'Confirm selection' : 'Select from left';
		},
		showCommittedImageControls (): boolean {
			return shouldShowCommittedImageControls({
				hasCommittedImages: this.hasImages,
				isSelectionActive: this.isThisFieldSelectionActive,
			});
		},
		hasPose3D (): boolean {
			return !!this._get(this.field, 'display.hasPose3D');
		},
		fieldUuid (): string | null {
			return this._get(this.field, 'uuid') || null;
		},
	},
	data () {
		return {
			IMAGE_UPLOAD_MODE,
		};
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'updateVisualPromptEditorState',
			'updatePose3DEditorState',
			'updateSnackbar',
		]),
		onClear () {
			this.$emit('on-clear');
		},
		onLibrary () {
			this.$emit('on-library');
		},
		toggleImageSelectionMode () {
			this.$emit('on-toggle-image-selection-mode');
		},
		syncSelectionState () {
			if (!this.field) return;

			const currentUploads = this._get(this.value, 'uploads') || [];
			const currentNodeRunResults = this._get(this.value, 'nodeRunResults') || [];

			// Build selectedNodeRunsMap from nodeRunResults
			const selectedNodeRunsMap: Record<string, { nodeRun: any, results: RunnitNodeRunResult[] }> = {};
			currentNodeRunResults.forEach((nodeRunResult: RunnitNodeRunResult) => {
				const nodeRunId = nodeRunResult.nodeRunId;
				if (!selectedNodeRunsMap[nodeRunId]) {
					selectedNodeRunsMap[nodeRunId] = {
						nodeRun: { id: nodeRunId },
						results: [],
					};
				}
				selectedNodeRunsMap[nodeRunId].results.push(nodeRunResult);
			});

			this.updateSelectionState({
				triggeringId: this.selectionStateTriggerId,
				sortOrder: 0,
				nodeRunResultSelectionMode: NODE_RUN_RESULT_SELECTION_MODE.MULTIPLE,
				canSelectTypes: ['IMG'],
				fieldAwaitingImageGallerySelection: this.field,
				selectedNodeRunsMap,
				selectedNodeRunResults: currentNodeRunResults,
				selectedLoadedAssets: [],
				selectedUploads: currentUploads,
				selectedAvatars: [],
			});
			this.enterSelectionMode(NODE_RUN_RESULT_SELECTION_MODE.MULTIPLE, this.selectionStateTriggerId, this.field, ['IMG']);
		},
		async onAnnotate () {
			if (!this.hasVisualPrompt) return;
			// Skip the selection dialog when there's only one image — open the
			// Visual Prompt Editor directly. MultiImageInput's editor handles
			// the replace-image emit and update flow.
			const uploads = this._get(this.value, 'uploads') || [];
			const nodeRunResults = this._get(this.value, 'nodeRunResults') || [];

			if (uploads.length + nodeRunResults.length === 1) {
				const singleUpload = uploads[0] || null;
				const singleNodeRunResult = nodeRunResults[0] || null;
				const baseSource = annotationSourceReference(singleNodeRunResult || singleUpload, singleNodeRunResult ? 'nodeRunResult' : 'upload');
				if (!baseSource) return;

				let imageUrl: string | null = null;
				try {
					if (singleNodeRunResult) {
						const { imageUrl: url } = await this.fetchSrc(singleNodeRunResult, 'nodeRunResult');
						imageUrl = url;
					} else if (singleUpload) {
						const { imageUrl: url } = await this.fetchSrc(singleUpload, 'upload');
						imageUrl = url;
					}
				} catch (err) {
					console.error('Error fetching image URL for visual prompt editor:', err);
					return;
				}

				if (!imageUrl) return;

				this.updateRunnitState({
					promptGeneratorOpen: false,
					imageSelectOpen: false,
				});

				// Set replaceImageTarget so VisualPromptEditor emits
				// 'replace-image' on save — MultiImageInput's editor only
				// wires @replace-image.
				this.updateVisualPromptEditorState({
					fieldUuid: this.field.uuid,
					editorState: {
						open: true,
						backgroundImageUrl: imageUrl,
						baseSource,
						replaceImageTarget: singleNodeRunResult
							? { kind: 'nodeRunResult', nodeRunResultUuid: singleNodeRunResult.uuid }
							: { kind: 'upload', uploadId: singleUpload.id },
					},
				});
				return;
			}

			this.syncSelectionState();
			this.updateRunnitState({ selectedImagesDialogOpen: true });
		},
		handleImageUpload (files: UploadFile[]) {
			this.$emit('on-upload', files);
		},
		onUse3D () {
			if (this.maxImages && this.allImages.length >= this.maxImages) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Maximum of ${this.maxImages} images allowed`,
					show: true,
				});
				return;
			}
			const input = this.$refs.pose3dFileInput as HTMLInputElement | undefined;
			if (input) input.click();
		},
		onPose3DFileSelected (ev: Event) {
			const target = ev.target as HTMLInputElement;
			const file = target.files && target.files[0];
			target.value = '';
			if (!file || !this.fieldUuid) return;
			this.updatePose3DEditorState({
				fieldUuid: this.fieldUuid,
				editorState: {
					open: true,
					saving: false,
					assetFile: file,
					assetName: file.name,
				},
			});
		},
		removeImage (index: number) {
			this.$emit('on-remove', index);
		},
		getImageKey (image: any, index: number) {
			if (image.upload) {
				return `upload-${image.upload.id}-${index}`;
			}
			if (image.nodeRunResult) {
				return `result-${image.nodeRunResult.uuid}-${index}`;
			}
			return `img-${index}`;
		},
		getStackStyle (index: number) {
			// Index 0 is front, Index 1 behind, etc.
			// Z-index: Highest for 0
			const zIndex = 10 - index;
			// Offset: 0 for 0, 6px for 1, 12px for 2
			const offset = index * 6;

			return {
				zIndex,
				bottom: `${offset}px`,
				left: `${offset}px`,
			};
		},
	},
	components: {
		BaseButton,
		ImageUpload,
		RunnitSelectedImage,
		Use3DTooltipCard,
	},
});
